import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { scrollToSection } from "@/hooks/useLenis";
import { CONTACT } from "@/lib/content";

const NAV = [
  { label: "Services", id: "services" },
  { label: "Before & After", id: "before-after" },
  { label: "Service Map", id: "service-area" },
  { label: "Manifesto", id: "manifesto" },
];

export const Header = ({ onQuote }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToSection(id), 400);
    } else {
      scrollToSection(id);
    }
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="fixed top-0 left-0 right-0 z-50 px-4 sm:px-6 lg:px-8 pt-4"
    >
      <div
        className={`max-w-7xl mx-auto flex items-center justify-between rounded-full px-4 sm:px-6 py-3 transition-[background-color,box-shadow,border-color] duration-500 border ${
          scrolled
            ? "bg-white/80 backdrop-blur-xl border-black/10 shadow-[0_10px_40px_-15px_rgba(0,0,0,0.25)]"
            : "bg-white/40 backdrop-blur-md border-white/40"
        }`}
      >
        <button
          data-testid="nav-logo"
          onClick={() => (location.pathname === "/" ? scrollToSection("hero") : navigate("/"))}
          className="flex items-center gap-2 group"
        >
          <span className="grid place-items-center w-12 h-12 rounded-xl bg-black overflow-hidden">
            <img src="https://customer-assets-0z36b82j.emergentagent.net/job_cba-services-hub/artifacts/6hjjzput_IMG_4077.jpeg" alt="CBA Mowing logo" className="w-full h-full object-cover" />
          </span>
          <span className="font-display font-extrabold text-base tracking-tight leading-[0.92] flex flex-col">
            <span>CBA</span>
            <span className="text-[#05C46B]">Mowing</span>
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((n) => (
            <button
              key={n.id}
              data-testid={`nav-link-${n.id}`}
              onClick={() => go(n.id)}
              className="font-mono-t text-[11px] uppercase tracking-widest text-[#52635A] hover:text-[#0B0F0C] transition-colors link-underline"
            >
              {n.label}
            </button>
          ))}
          <button
            data-testid="nav-link-about"
            onClick={() => navigate("/about")}
            className="font-mono-t text-[11px] uppercase tracking-widest text-[#52635A] hover:text-[#0B0F0C] transition-colors link-underline"
          >
            About Us
          </button>
        </nav>

        <div className="flex items-center gap-2">
          <a
            data-testid="nav-phone"
            href={`tel:${CONTACT.phone}`}
            className="hidden sm:flex items-center gap-2 text-sm font-medium text-[#0B0F0C] px-3 py-2 rounded-full hover:bg-black/5 transition-colors"
          >
            <Phone className="w-4 h-4" /> {CONTACT.phone}
          </a>
          <button
            data-testid="nav-cta-quote"
            onClick={onQuote}
            className="hidden sm:inline-flex items-center rounded-full bg-[#0B0F0C] text-white text-sm font-semibold px-5 py-2.5 hover:bg-[#05C46B] transition-colors duration-300"
          >
            Get Free Quote
          </button>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
            className="lg:hidden grid place-items-center w-10 h-10 rounded-full bg-black/5"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="lg:hidden max-w-7xl mx-auto mt-2 bg-white rounded-3xl border border-black/10 p-5 shadow-xl"
          >
            <div className="flex flex-col gap-1">
              {NAV.map((n) => (
                <button
                  key={n.id}
                  data-testid={`nav-mobile-${n.id}`}
                  onClick={() => go(n.id)}
                  className="text-left py-3 font-display font-semibold text-lg border-b border-black/5"
                >
                  {n.label}
                </button>
              ))}
              <button
                onClick={() => { setOpen(false); navigate("/about"); }}
                className="text-left py-3 font-display font-semibold text-lg border-b border-black/5"
              >
                About Us
              </button>
              <button
                data-testid="nav-mobile-quote"
                onClick={() => { setOpen(false); onQuote(); }}
                className="mt-4 rounded-full bg-[#0B0F0C] text-white font-semibold py-3"
              >
                Get Free Quote
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
